/**
 * Comprehensive debugging utilities for Lambe Turah News
 * Helps track image loading issues, API problems, and performance metrics
 */

interface DebugLog {
  timestamp: number;
  type: 'info' | 'warning' | 'error' | 'success';
  category: string;
  message: string;
  data?: any;
  stack?: string;
}

interface ImageDebugInfo {
  src: string;
  alt: string;
  componentId: string;
  state: 'loading' | 'loaded' | 'error' | 'retry';
  retryCount: number;
  loadTime?: number;
  errorMessage?: string;
  dimensions?: { width: number; height: number };
}

interface APIDebugInfo {
  url: string;
  method: string;
  status?: number;
  duration?: number;
  error?: string;
  requestId: string;
  timestamp: number;
  category?: string;
}

class DebugUtils {
  private static logs: DebugLog[] = [];
  private static imageTracking: Map<string, ImageDebugInfo> = new Map();
  private static apiTracking: Map<string, APIDebugInfo> = new Map();
  private static maxLogs = 1000;
  private static isEnabled = process.env.NODE_ENV === 'development';

  /**
   * Initialize debug utilities and expose global functions
   */
  static initialize() {
    if (typeof window === 'undefined') return;

    // Expose debugging functions globally
    (window as any).ltnDebug = {
      // General logging
      getLogs: () => this.getAllLogs(),
      clearLogs: () => this.clearLogs(),
      downloadLogs: () => this.downloadLogs(),

      // Image debugging
      getImageStats: () => this.getImageStats(),
      getFailedImages: () => this.getFailedImages(),
      retryFailedImages: () => this.retryFailedImages(),
      trackImage: (info: Partial<ImageDebugInfo>) => this.trackImage(info),

      // API debugging
      getAPIStats: () => this.getAPIStats(),
      getFailedAPICalls: () => this.getFailedAPICalls(),
      clearAPITracking: () => this.clearAPITracking(),

      // Performance monitoring
      getPerformanceMetrics: () => this.getPerformanceMetrics(),
      startPerformanceMonitoring: () => this.startPerformanceMonitoring(),

      // Cache debugging
      inspectQueryCache: () => this.inspectQueryCache(),
      analyzeMemoryUsage: () => this.analyzeMemoryUsage(),

      // Network debugging
      simulateOffline: () => this.simulateOffline(),
      simulateSlowNetwork: (delay: number) => this.simulateSlowNetwork(delay),

      // Utility functions
      generateReport: () => this.generateDebugReport(),
      enable: () => this.enable(),
      disable: () => this.disable(),
    };

    // Debug utilities initialized silently
  }

  /**
   * Log a debug message
   */
  static log(
    type: DebugLog['type'],
    category: string,
    message: string,
    data?: any
  ): void {
    if (!this.isEnabled) return;

    const log: DebugLog = {
      timestamp: Date.now(),
      type,
      category,
      message,
      data,
      stack: type === 'error' ? new Error().stack : undefined,
    };

    this.logs.push(log);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Only log errors and warnings to console to reduce spam
    if (type === 'error' || type === 'warning') {
      const style = this.getConsoleStyle(type);
      console.groupCollapsed(`%c[${category}] ${message}`, style);
      if (data) console.log('Data:', data);
      if (log.stack) console.log('Stack:', log.stack);
      console.groupEnd();
    }
  }

  /**
   * Track image loading
   */
  static trackImage(info: Partial<ImageDebugInfo>): void {
    if (!this.isEnabled || !info.src) return;

    const key = `${info.componentId}-${info.src}`;
    const existing = this.imageTracking.get(key);

    const updated: ImageDebugInfo = {
      src: info.src,
      alt: info.alt || '',
      componentId: info.componentId || 'unknown',
      state: info.state || 'loading',
      retryCount: info.retryCount || 0,
      loadTime: info.loadTime || existing?.loadTime,
      errorMessage: info.errorMessage || existing?.errorMessage,
      dimensions: info.dimensions || existing?.dimensions,
    };

    this.imageTracking.set(key, updated);

    this.log('info', 'ImageTracker', `Image ${info.state}`, {
      src: info.src,
      componentId: info.componentId,
      state: info.state,
      retryCount: info.retryCount,
    });
  }

  /**
   * Track API calls
   */
  static trackAPI(info: APIDebugInfo): void {
    if (!this.isEnabled) return;

    this.apiTracking.set(info.requestId, info);

    const logType = info.error ? 'error' : info.status && info.status >= 400 ? 'warning' : 'info';

    this.log(logType, 'APITracker', `${info.method} ${info.url}`, {
      status: info.status,
      duration: info.duration,
      error: info.error,
      category: info.category,
    });
  }

  /**
   * Get all logs
   */
  static getAllLogs(): DebugLog[] {
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  static clearLogs(): void {
    this.logs = [];
    // Logs cleared silently
  }

  /**
   * Download logs as JSON file
   */
  static downloadLogs(): void {
    const data = {
      timestamp: new Date().toISOString(),
      logs: this.logs,
      imageTracking: Array.from(this.imageTracking.entries()),
      apiTracking: Array.from(this.apiTracking.entries()),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ltn-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Get image statistics
   */
  static getImageStats() {
    const images = Array.from(this.imageTracking.values());

    return {
      total: images.length,
      loaded: images.filter(img => img.state === 'loaded').length,
      loading: images.filter(img => img.state === 'loading').length,
      error: images.filter(img => img.state === 'error').length,
      retrying: images.filter(img => img.state === 'retry').length,
      averageLoadTime: this.calculateAverageLoadTime(images),
      highRetryCount: images.filter(img => img.retryCount > 2).length,
      details: images,
    };
  }

  /**
   * Get failed images
   */
  static getFailedImages(): ImageDebugInfo[] {
    return Array.from(this.imageTracking.values()).filter(img => img.state === 'error');
  }

  /**
   * Retry failed images by triggering a custom event
   */
  static retryFailedImages(): void {
    const failedImages = this.getFailedImages();

    failedImages.forEach(img => {
      const event = new CustomEvent('retryImage', {
        detail: { componentId: img.componentId, src: img.src }
      });
      window.dispatchEvent(event);
    });

    console.log(`[DebugUtils] Retry triggered for ${failedImages.length} failed images`);
  }

  /**
   * Get API statistics
   */
  static getAPIStats() {
    const calls = Array.from(this.apiTracking.values());

    return {
      total: calls.length,
      successful: calls.filter(call => !call.error && call.status && call.status < 400).length,
      failed: calls.filter(call => call.error || (call.status && call.status >= 400)).length,
      averageResponseTime: this.calculateAverageResponseTime(calls),
      slowCalls: calls.filter(call => call.duration && call.duration > 3000).length,
      by503Errors: calls.filter(call => call.status === 503).length,
      byCategory: this.groupAPICallsByCategory(calls),
      details: calls,
    };
  }

  /**
   * Get failed API calls
   */
  static getFailedAPICalls(): APIDebugInfo[] {
    return Array.from(this.apiTracking.values()).filter(
      call => call.error || (call.status && call.status >= 400)
    );
  }

  /**
   * Clear API tracking
   */
  static clearAPITracking(): void {
    this.apiTracking.clear();
    console.log('[DebugUtils] API tracking cleared');
  }

  /**
   * Get performance metrics
   */
  static getPerformanceMetrics() {
    if (typeof window === 'undefined' || !window.performance) {
      return { error: 'Performance API not available' };
    }

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

    return {
      loadTime: navigation.loadEventEnd - navigation.loadEventStart,
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      firstPaint: this.getFirstPaint(),
      largestContentfulPaint: this.getLargestContentfulPaint(),
      cumulativeLayoutShift: this.getCumulativeLayoutShift(),
      memoryUsage: this.getMemoryUsage(),
    };
  }

  /**
   * Start performance monitoring
   */
  static startPerformanceMonitoring(): void {
    if (typeof window === 'undefined') return;

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              this.log('warning', 'Performance', 'Long task detected', {
                duration: entry.duration,
                startTime: entry.startTime,
              });
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        console.warn('[DebugUtils] Could not start long task monitoring:', e);
      }
    }

    // Monitor large layout shifts
    if ('LayoutShift' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const layoutShift = entry as any;
            if (layoutShift.value > 0.1) {
              this.log('warning', 'Performance', 'Large layout shift detected', {
                value: layoutShift.value,
                sources: layoutShift.sources,
              });
            }
          }
        });
        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('[DebugUtils] Could not start layout shift monitoring:', e);
      }
    }
  }

  /**
   * Inspect React Query cache
   */
  static inspectQueryCache() {
    const ltnApp = (window as any).ltnApp;
    if (!ltnApp?.queryClient) {
      return { error: 'Query client not available' };
    }

    const cache = ltnApp.getQueryCache();
    const queries = ltnApp.getAllQueries();

    return {
      totalQueries: queries.length,
      queries: queries.map((query: any) => ({
        queryKey: query.queryKey,
        state: query.state,
        dataUpdatedAt: query.state.dataUpdatedAt,
        isStale: query.isStale(),
        isFetching: query.state.isFetching,
        error: query.state.error?.message,
      })),
      cacheSize: this.estimateCacheSize(cache),
    };
  }

  /**
   * Analyze memory usage
   */
  static analyzeMemoryUsage() {
    if (typeof window === 'undefined') return { error: 'Window not available' };

    const memory = this.getMemoryUsage();
    const performance = window.performance as any;

    return {
      memory,
      resourceCount: performance.getEntriesByType('resource').length,
      domNodes: document.querySelectorAll('*').length,
      eventListeners: this.estimateEventListeners(),
      images: {
        total: document.querySelectorAll('img').length,
        loaded: document.querySelectorAll('img[complete="true"]').length,
        loading: document.querySelectorAll('img:not([complete="true"])').length,
      },
    };
  }

  /**
   * Simulate offline mode
   */
  static simulateOffline(): void {
    // This would need service worker support for full simulation
    Object.defineProperty(navigator, 'onLine', {
      value: false,
      writable: false,
    });

    window.dispatchEvent(new Event('offline'));
    console.log('[DebugUtils] Offline mode simulated');
  }

  /**
   * Simulate slow network
   */
  static simulateSlowNetwork(delay: number): void {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      await new Promise(resolve => setTimeout(resolve, delay));
      return originalFetch(...args);
    };

    console.log(`[DebugUtils] Slow network simulated with ${delay}ms delay`);
  }

  /**
   * Generate comprehensive debug report
   */
  static generateDebugReport() {
    return {
      timestamp: new Date().toISOString(),
      browser: {
        userAgent: navigator.userAgent,
        onLine: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
        language: navigator.language,
      },
      page: {
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
      },
      performance: this.getPerformanceMetrics(),
      images: this.getImageStats(),
      api: this.getAPIStats(),
      memory: this.analyzeMemoryUsage(),
      cache: this.inspectQueryCache(),
      recentLogs: this.logs.slice(-50), // Last 50 logs
    };
  }

  /**
   * Enable debugging
   */
  static enable(): void {
    this.isEnabled = true;
    // Debugging enabled silently
  }

  /**
   * Disable debugging
   */
  static disable(): void {
    this.isEnabled = false;
    // Debugging disabled silently
  }

  // Private helper methods

  private static getConsoleStyle(type: DebugLog['type']): string {
    const styles = {
      info: 'color: #2196F3; font-weight: bold;',
      warning: 'color: #FF9800; font-weight: bold;',
      error: 'color: #F44336; font-weight: bold;',
      success: 'color: #4CAF50; font-weight: bold;',
    };
    return styles[type];
  }

  private static calculateAverageLoadTime(images: ImageDebugInfo[]): number {
    const loadedImages = images.filter(img => img.loadTime);
    if (loadedImages.length === 0) return 0;

    const total = loadedImages.reduce((sum, img) => sum + (img.loadTime || 0), 0);
    return Math.round(total / loadedImages.length);
  }

  private static calculateAverageResponseTime(calls: APIDebugInfo[]): number {
    const completedCalls = calls.filter(call => call.duration);
    if (completedCalls.length === 0) return 0;

    const total = completedCalls.reduce((sum, call) => sum + (call.duration || 0), 0);
    return Math.round(total / completedCalls.length);
  }

  private static groupAPICallsByCategory(calls: APIDebugInfo[]) {
    return calls.reduce((acc, call) => {
      const category = call.category || 'unknown';
      if (!acc[category]) {
        acc[category] = { total: 0, failed: 0 };
      }
      acc[category].total++;
      if (call.error || (call.status && call.status >= 400)) {
        acc[category].failed++;
      }
      return acc;
    }, {} as Record<string, { total: number; failed: number }>);
  }

  private static getFirstPaint(): number | null {
    const paintEntries = performance.getEntriesByType('paint');
    const firstPaint = paintEntries.find(entry => entry.name === 'first-paint');
    return firstPaint ? firstPaint.startTime : null;
  }

  private static getLargestContentfulPaint(): number | null {
    const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
    return lcpEntries.length > 0 ? lcpEntries[lcpEntries.length - 1].startTime : null;
  }

  private static getCumulativeLayoutShift(): number {
    const clsEntries = performance.getEntriesByType('layout-shift') as any[];
    return clsEntries.reduce((sum, entry) => sum + entry.value, 0);
  }

  private static getMemoryUsage() {
    const memory = (performance as any).memory;
    if (!memory) return { error: 'Memory API not available' };

    return {
      used: Math.round(memory.usedJSHeapSize / 1024 / 1024 * 100) / 100,
      total: Math.round(memory.totalJSHeapSize / 1024 / 1024 * 100) / 100,
      limit: Math.round(memory.jsHeapSizeLimit / 1024 / 1024 * 100) / 100,
    };
  }

  private static estimateCacheSize(cache: any): string {
    try {
      const size = JSON.stringify(cache).length;
      return `~${Math.round(size / 1024 * 100) / 100} KB`;
    } catch {
      return 'Unable to estimate';
    }
  }

  private static estimateEventListeners(): number {
    // This is an approximation - exact count would require more complex tracking
    const elements = document.querySelectorAll('*');
    let count = 0;

    elements.forEach(el => {
      const events = ['click', 'scroll', 'load', 'error', 'focus', 'blur'];
      events.forEach(event => {
        if ((el as any)[`on${event}`]) count++;
      });
    });

    return count;
  }
}

// Initialize on import
DebugUtils.initialize();

export default DebugUtils;

// Export specific functions for direct import
export const {
  log,
  trackImage,
  trackAPI,
  getImageStats,
  getAPIStats,
  generateDebugReport,
} = DebugUtils;
